import React, {useCallback, useState} from 'react';
import {EnvelopeIcon, IconButton, Position, TickIcon, Tooltip} from 'evergreen-ui';
import {PORTAL_NAVY} from '../theme';

/**
 * Decorates an email label with two affordances (BHPNPP-3107 / BHPNPP-3108):
 *   1. hover reveals the underlying address via a tooltip, and
 *   2. a copy-to-clipboard button beside it.
 *
 * The caller owns the visible label and passes it as {@link children} — a plain name, or text plus an
 * icon, or a `mailto:` link — so the same component serves the Roster "Uploaded By" cell and the
 * Client Details team links without either consumer dictating the other's look. The component adds
 * only the reveal tooltip and the copy button; it does not render the label itself.
 */
type Props = {
    /** The address to reveal on hover and copy. */
    email: string;
    /** The visible label the email hides behind. */
    children: React.ReactNode;
    /**
     * Glyph for the copy button. Defaults to the envelope, because the label is usually a person's
     * name and the envelope is what says the name hides an address. One glyph on every page keeps
     * the pattern recognisable.
     */
    copyIcon?: React.ElementType;
};

// How long the button shows its "copied" confirmation before reverting to the copy glyph.
const COPIED_RESET_MS = 1500;

const PORTAL_LINK_BLUE = '#5566FE';

// Teams-style email card: white background, navy border, soft shadow. Overrides evergreen's default
// slate tooltip (whose dark paragraph on a slate card read at ~1.6:1) and removes its 240px max-width
// so a full email address never truncates. These are Box props, NOT an inline `style` object: evergreen
// positions the tooltip card through its own inline styles, so passing `style` clobbers them and the
// card renders off-screen. The navy border is drawn with a hairline box-shadow (`0 0 0 1px`) rather
// than `border` — evergreen's Box resolves `border` through theme tokens (a raw CSS string is dropped),
// and box-shadow doesn't affect the box model or positioning.
const TOOLTIP_CARD_PROPS = {
    backgroundColor: '#FFFFFF',
    maxWidth: 'none',
    boxShadow: `0 0 0 1px ${PORTAL_NAVY}, 0 2px 8px rgba(0, 0, 0, 0.15)`,
};

// Content is passed as a coloured, non-wrapping node (not a bare string) so evergreen skips its dark
// default paragraph and the address shows on one line.
const tooltipText = (text: string, color: string) =>
    <span style={{color, whiteSpace: 'nowrap'}}>{text}</span>;

export function EmailWithCopy({email, children, copyIcon = EnvelopeIcon}: Props) {
    const [copied, setCopied] = useState(false);

    const copy = useCallback(async () => {
        // HTTPS-only app, so navigator.clipboard is present; guard anyway for insecure-context / older
        // browsers — the address stays reachable through the hover tooltip, so a no-op is acceptable.
        if (!navigator.clipboard) {
            return;
        }
        try {
            await navigator.clipboard.writeText(email);
            setCopied(true);
            setTimeout(() => setCopied(false), COPIED_RESET_MS);
        } catch {
            // Swallow: copy is a convenience, not a critical path.
        }
    }, [email]);

    // Laid out inline rather than as a flex row, so a long label still wraps inside a narrow table
    // cell. The non-breaking space keeps the button on the label's last line.
    return (
        <span>
            <Tooltip content={tooltipText(email, PORTAL_LINK_BLUE)} position={Position.TOP}
                     statelessProps={TOOLTIP_CARD_PROPS}>
                <span>{children}</span>
            </Tooltip>
            {'\u00A0'}
            <Tooltip content={tooltipText(copied ? 'Copied!' : 'Copy email', PORTAL_NAVY)} position={Position.TOP}
                     statelessProps={TOOLTIP_CARD_PROPS}>
                <IconButton
                    type="button"
                    appearance="minimal"
                    height={24}
                    iconSize={14}
                    icon={copied ? TickIcon : copyIcon}
                    intent={copied ? 'success' : 'none'}
                    onClick={copy}
                    aria-label={`Copy email ${email}`}
                    // Evergreen's button is display:flex, which claims a line of its own. Inline-flex
                    // keeps it beside the label.
                    display="inline-flex"
                    // Baseline, not evergreen's middle: an inline-flex box takes its baseline from
                    // the glyph inside it, and an svg's baseline is its own bottom edge, so the
                    // glyph comes to rest on the line the label's letters sit on. Middle centres the
                    // button box instead, which hangs the glyph three-odd pixels below the text.
                    // Written as an inline style because evergreen's own class sets vertical-align
                    // and the verticalAlign box prop loses to it.
                    style={{verticalAlign: 'baseline'}}
                />
            </Tooltip>
        </span>
    );
}

/**
 * Convenience wrapper over {@link EmailWithCopy} for the common "user email column" case (Roster
 * "Uploaded By", and similar people columns going forward): pass plain {@link label} text and it
 * renders as a portal-blue `mailto:` link, hover-reveal + copy included. Reach for the underlying
 * {@link EmailWithCopy} directly when the label needs custom markup (e.g. text plus an icon).
 */
type LabeledProps = {
    /** The address to reveal, copy, and link to. */
    email: string;
    /** Plain label text (e.g. a person's name) rendered as the portal-blue link. */
    label: string;
    /** Glyph for the copy button - see {@link Props#copyIcon}. */
    copyIcon?: React.ElementType;
};

export function EmailLabelWithCopy({email, label, copyIcon}: LabeledProps) {
    return (
        <EmailWithCopy email={email} copyIcon={copyIcon}>
            <a href={`mailto:${email}`} className="text-blue-portal-link">{label}</a>
        </EmailWithCopy>
    );
}
